import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { FEATURE_FLAG_SKIP_ENLIGHTEN_PRO_LICENSE_CHECK } from "../../../../common/features";
import { useFeatureFlagEnabled } from "../../../../common/posthog";
import { UserLicenseStatus } from "../../../../data/queries/userLicense";
import { useAuth } from "../../../auth/AuthContext";
import {
  LICENSE_ACTIVE_DESCRIPTION,
  LICENSE_UNAVAILABLE_MESSAGE,
  LICENSE_WARNING_MESSAGE
} from "../../constants/license";
import { useUserLicenses } from "../../hooks/useUserLicenses";
import { AnalysisEntity, AnalysisProfile } from "../../types";
import { isValidEmailFormat } from "../../utils/emailValidation";

interface EntityDialogProps {
  open: boolean;
  entity: AnalysisEntity | null;
  profiles: AnalysisProfile[];
  existingEmails: { email: string; is_deleted: boolean }[];
  onClose: () => void;
  onSave: (entity: Partial<AnalysisEntity>) => void;
}

interface ValidationInfo {
  type: "error" | "info";
  message: string;
}

const EMAIL_DEBOUNCE_MS = 400;

interface LicenseBlockInputs {
  tenantId: string | null;
  licenseError: Error | null;
  licenseLoading: boolean;
  licenseStatus: UserLicenseStatus | null;
}

const getLicenseBlockMessage = ({
  tenantId,
  licenseError,
  licenseLoading,
  licenseStatus
}: LicenseBlockInputs): ValidationInfo | null => {
  if (!tenantId) {
    return { type: "error", message: "Unable to verify license: missing tenant context." };
  }
  if (licenseError) {
    return { type: "error", message: LICENSE_UNAVAILABLE_MESSAGE };
  }
  if (licenseLoading || !licenseStatus) {
    return {
      type: "error",
      message: "Still verifying the Enlighten Pro license — please wait a moment and try again."
    };
  }
  if (!licenseStatus.hasEnlightenPro) {
    return { type: "error", message: LICENSE_WARNING_MESSAGE };
  }
  return null;
};

interface LicenseFieldProps {
  isLoading: boolean;
  hasEmail: boolean;
  licenseStatus: UserLicenseStatus | null;
  error: Error | null;
}

const LicenseField: React.FC<LicenseFieldProps> = ({
  isLoading,
  hasEmail,
  licenseStatus,
  error
}) => {
  const renderBody = (): React.ReactNode => {
    if (!hasEmail) {
      return (
        <Typography variant="body2" color="text.secondary">
          Enter an email to check the Enlighten Pro license.
        </Typography>
      );
    }
    if (isLoading) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Checking Spokephone license…
          </Typography>
        </Stack>
      );
    }
    if (error) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={LICENSE_UNAVAILABLE_MESSAGE}>
            <WarningAmberIcon color="warning" fontSize="small" />
          </Tooltip>
          <Typography variant="body2" color="warning.main">
            Could not verify license
          </Typography>
        </Stack>
      );
    }
    if (licenseStatus?.hasEnlightenPro) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CheckCircleIcon color="success" fontSize="small" />
          <Typography variant="body2" color="success.main">
            {LICENSE_ACTIVE_DESCRIPTION}
          </Typography>
        </Stack>
      );
    }
    if (licenseStatus) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningAmberIcon color="warning" fontSize="small" />
          <Typography variant="body2" color="warning.main">
            No Enlighten Pro license in Spokephone
          </Typography>
        </Stack>
      );
    }
    return null;
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        License
      </Typography>
      <Box mt={0.5}>{renderBody()}</Box>
    </Box>
  );
};

export const EntityDialog: React.FC<EntityDialogProps> = ({
  open,
  entity,
  profiles,
  existingEmails,
  onClose,
  onSave
}) => {
  const { tenantId } = useAuth();
  const skipLicenseCheck = useFeatureFlagEnabled(FEATURE_FLAG_SKIP_ENLIGHTEN_PRO_LICENSE_CHECK);
  const [formData, setFormData] = useState({
    email: "",
    analysis_profile_id: "",
    status: "active" as "active" | "disabled"
  });

  const [errors, setErrors] = useState({ email: "" });
  const [validationInfo, setValidationInfo] = useState<ValidationInfo | null>(null);
  const [debouncedEmail, setDebouncedEmail] = useState("");

  const trimmedEmail = formData.email.trim();
  const isEmailValid = trimmedEmail.length > 0 && isValidEmailFormat(trimmedEmail);

  useEffect(() => {
    setErrors({ email: "" });
    setValidationInfo(null);
    if (!entity) {
      setFormData({
        email: "",
        analysis_profile_id: "",
        status: "active"
      });
      setDebouncedEmail("");
      return;
    }
    setFormData({
      email: entity.email,
      analysis_profile_id: entity.analysis_profile_id || "",
      status: entity.status
    });
    setDebouncedEmail(entity.email);
  }, [entity, open]);

  useEffect(() => {
    if (entity) {
      return;
    }
    if (!isEmailValid) {
      setDebouncedEmail("");
      return;
    }
    const handle = window.setTimeout(() => {
      setDebouncedEmail(trimmedEmail);
    }, EMAIL_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [trimmedEmail, isEmailValid, entity]);

  const emailsToCheck = useMemo(
    () => (debouncedEmail && !skipLicenseCheck ? [debouncedEmail] : []),
    [debouncedEmail, skipLicenseCheck]
  );

  const {
    resultByEmail,
    isLoading: licenseLoading,
    error: licenseError
  } = useUserLicenses(emailsToCheck);

  const licenseStatus = debouncedEmail
    ? resultByEmail[debouncedEmail.toLowerCase()] ?? null
    : null;

  const checkEmailExistence = (email: string): ValidationInfo | null => {
    if (!email || entity) {
      return null;
    }

    const lowerEmail = email.toLowerCase();
    const existingEmail = existingEmails.find(e => e.email === lowerEmail);

    if (!existingEmail) {
      return null;
    }

    if (existingEmail.is_deleted) {
      return {
        type: "info",
        message: "This email was previously deleted and will be reactivated."
      };
    }
    return {
      type: "error",
      message: "This email address is already registered. Please use a different email address."
    };
  };

  const handleSubmit = () => {
    if (!formData.email) {
      setErrors({ email: "Email is required" });
      setValidationInfo(null);
      return;
    }

    if (!isValidEmailFormat(formData.email)) {
      setErrors({ email: "Please enter a valid email address" });
      setValidationInfo(null);
      return;
    }

    const existenceCheck = checkEmailExistence(formData.email);
    if (existenceCheck?.type === "error") {
      setErrors({ email: existenceCheck.message });
      setValidationInfo(null);
      return;
    }

    const requiresLicenseCheck = !entity && !skipLicenseCheck;
    const submittingEmail = formData.email.trim().toLowerCase();
    const licenseNotYetVerified = requiresLicenseCheck &&
      debouncedEmail.toLowerCase() !== submittingEmail;

    if (licenseNotYetVerified) {
      setValidationInfo({
        type: "error",
        message: "Still verifying the Enlighten Pro license — please wait a moment and try again."
      });
      return;
    }

    const licenseBlock = requiresLicenseCheck
      ? getLicenseBlockMessage({ tenantId, licenseError, licenseLoading, licenseStatus })
      : null;

    if (licenseBlock) {
      setValidationInfo(licenseBlock);
      return;
    }

    setErrors({ email: "" });
    setValidationInfo(existenceCheck);

    onSave({
      ...formData,
      email: formData.email.toLowerCase(),
      analysis_profile_id: formData.analysis_profile_id || null
    });
  };

  const handleEmailChange = (email: string) => {
    setFormData({ ...formData, email });
    setErrors({ email: "" });

    setValidationInfo(email && isValidEmailFormat(email) ? checkEmailExistence(email) : null);
  };

  const isEditing = Boolean(entity);
  const hasLicenseWithoutPro = licenseStatus !== null && !licenseStatus.hasEnlightenPro && !licenseError;
  const showLicenseWarningBanner = !skipLicenseCheck && isEditing && hasLicenseWithoutPro;
  const showLicenseFailedBanner = !skipLicenseCheck && isEditing && Boolean(licenseError);

  const emailSettling = isEmailValid && debouncedEmail.toLowerCase() !== trimmedEmail.toLowerCase();
  const licenseNotReady = !skipLicenseCheck && (licenseLoading || emailSettling);
  const submitDisabled = !entity && (!isEmailValid || licenseNotReady);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{entity ? "Edit Person" : "Add Person"}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {showLicenseWarningBanner && (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              {LICENSE_WARNING_MESSAGE}
            </Alert>
          )}

          {showLicenseFailedBanner && (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              {LICENSE_UNAVAILABLE_MESSAGE}
            </Alert>
          )}

          {validationInfo && (
            <Alert severity={validationInfo.type === "error" ? "error" : "info"}>
              {validationInfo.message}
            </Alert>
          )}

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleEmailChange(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            required
          />

          {!skipLicenseCheck && (
            <LicenseField
              isLoading={licenseLoading}
              hasEmail={debouncedEmail.length > 0}
              licenseStatus={licenseStatus}
              error={licenseError}
            />
          )}

          <FormControl fullWidth>
            <InputLabel>Analysis Group</InputLabel>
            <Select
              value={formData.analysis_profile_id}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  analysis_profile_id: e.target.value
                })
              }
              label="Analysis Group"
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {profiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as "active" | "disabled"
                })
              }
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitDisabled}
          startIcon={!entity && licenseLoading && !skipLicenseCheck ? <CircularProgress size={16} /> : null}
        >
          {!entity && licenseLoading && !skipLicenseCheck ? "Checking license…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
